<template>
  <div class="custom-options-personalization">
    <template v-if="isCustomForm || customPersonalization">
      <!-- custom options personalization for product no use customily -->
      <p
        v-if="isYourDesignProductType"
        class="product-personalization-no-preview-note"
      >
        After receiving your details, our team will evaluate its feasibility and
        send a design cost quote. Upon your approval, we'll provide a payment
        link. Once paid, we'll start the design, maintaining regular
        communication with you. Please note, exceptionally challenging or
        unfeasible requests might be declined.
      </p>
      <h2>Personalization</h2>

      <sib-skeleton-loading
        v-if="isCustomForm && !customPersonalization"
        class="product__preview-loading"
        height="100px"
      ></sib-skeleton-loading>

      <template v-else>
        <div class="custom-options-fields">
          <div v-for="(field, i) in customPersonalization" :key="i">
            <sib-form-item :label="field.label">
              <CustomField :field="field" />
            </sib-form-item>
          </div>
        </div>
        <template v-if="hasCustomUpload">
          <p
            v-if="
              !isYourDesignProductType &&
              !isProductTypeHiddenNotice &&
              !isProductTagHiddenNotice
            "
            class="product-personalization-no-preview-note"
          >
            {{ productPersonalizationNoPreviewNote }}
          </p>
          <input
            v-if="
              !isYourDesignProductType &&
              !isProductTypeHiddenNotice &&
              !isProductTagHiddenNotice
            "
            type="hidden"
            :name="`properties[${NO_PREVIEW_PERSONALIZATION}]`"
            value="no-preview"
          />
        </template>
      </template>
    </template>
    <client-only>
      <slot></slot>
    </client-only>

    <!-- custom field blog form -->
    <template v-if="isCustomBlogForm">
      <div
        class="custom-options-fields"
        v-if="customPersonalizationBlogForm.length"
      >
        <div v-for="(field, i) in customPersonalizationBlogForm" :key="i">
          <sib-form-item :label="field.label">
            <CustomField :field="field" />
          </sib-form-item>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import useProductStore from '@/store/product'
import useSettingStore from '@/store/setting'
import { computed, h, reactive } from 'vue'
import {
  getCustomPersonalization,
  isProductHasTags,
  isProductHasType,
} from '@/utils/product'
import SibFormItem from '@/ui-kits/FormItem.vue'
import { CustomizeField } from '@/types'
import {
  CUSTOMIZE_FIELD_TYPE_SINGLE_IMAGE,
  CUSTOMIZE_FIELD_TYPE_TEXT,
  CUSTOMIZE_FIELD_TYPE_TEXTAREA,
  CUSTOMIZE_FIELD_TYPE_MULTIPLE_IMAGE,
  CUSTOMIZE_FIELD_NOTE_TYPE_IMAGE,
  CUSTOMIZE_FIELD_TYPE_DROPDOWN,
  CUSTOMIZE_FIELD_TYPE_RADIO_GROUP,
  NO_PREVIEW_PERSONALIZATION,
  CUSTOMIZE_FIELD_NOTE_TYPE_TEXT,
  CUSTOMIZE_FIELD_TYPE_AUDIO,
  CUSTOMIZE_FIELD_TYPE_SEARCH_SONG,
  CUSTOMIZE_FIELD_TYPE_AUDIO_OR_SEARCH_SONG,
  CUSTOMIZE_FIELD_COLOR_PICKER,
  CUSTOMIZE_FIELD_TYPE_URL,
  PRODUCT_TYPE_YOUR_DESIGN,
  PRODUCT_TAG_HIDDEN_PREVIEW_NOTE,
} from '@/utils'
import Input from '@/ui-kits/Input.vue'
import Select from '@/ui-kits/Select.vue'
import RadioGroup from '@/ui-kits/RadioGroup.vue'
import ColorPicker from '@/ui-kits/ColorPicker.vue'
import FileUpload from '@/ui-kits/FileSelect.vue'
import FileUploadList from '@/ui-kits/FileUploadList.vue'
import CustomPersonalizationAudioOrSearchSpotifySong from './CustomPersonalizationAudioOrSearchSpotifySong.vue'
import SearchCustomUrl from './SearchCustomUrl.vue'
import Provider from '@/provider'
import Logger from '@/services/log'

const productStore = useProductStore()
const settingStore = useSettingStore()

const isCustomForm = computed(() => {
  return isProductHasTags(productStore.product, [
    settingStore.shop?.productTagCustomForm || '',
  ])
})

const isCustomBlogForm = computed(() => {
  return isProductHasTags(productStore.product, [
    settingStore.shop?.productTagCustomBlogForm || '',
  ])
})

const isYourDesignProductType = computed(() => {
  return isProductHasType(productStore?.product, [PRODUCT_TYPE_YOUR_DESIGN])
})

const isProductTypeHiddenNotice = computed(() => {
  const productTypesHiddenNote =
    settingStore.shop?.productPersonalizationNoPreviewNote
      ?.productTypesHiddenNote || []
  return isProductHasType(productStore?.product, productTypesHiddenNote)
})

const isProductTagHiddenNotice = computed(() => {
  return isProductHasTags(productStore.product, [
    PRODUCT_TAG_HIDDEN_PREVIEW_NOTE || '',
  ])
})

// custom field for custom blog
const customPersonalizationBlogForm = computed(() => {
  const customFieldsForm =
    productStore.productDescription?.upload_image?.blog_form

  return customFieldsForm ? JSON.parse(customFieldsForm) : []
})

// custom field for product no use customily
const customPersonalization = computed(() => {
  const customPersonalizations =
    settingStore.baseProductPage?.customPersonalizations
  const customFieldsForm =
    productStore.productDescription?.upload_image?.custom_form

  return isCustomForm.value && customFieldsForm
    ? JSON.parse(customFieldsForm)
    : getCustomPersonalization(productStore.product, customPersonalizations)
        ?.fields
})

const productPersonalizationNoPreviewNote = computed(
  () => settingStore.shop?.productPersonalizationNoPreviewNote?.note || ''
)
const hasCustomUpload = computed(() =>
  customPersonalization.value?.some(
    (field: CustomizeField) =>
      field?.type ==
      (CUSTOMIZE_FIELD_TYPE_MULTIPLE_IMAGE || CUSTOMIZE_FIELD_TYPE_SINGLE_IMAGE)
  )
)

const CustomField = ({ field }: { field: CustomizeField }) => {
  interface SelectedFile {
    file: File
    link?: string
    propertyName: string
    loading?: boolean
  }
  let tag: any = Input
  const props: {
    [key: string]: any
  } = {
    placeholder: field.placeholder || '',
    name: `properties[${field.name}]`,
    size: 'md',
    loading: false,
    attributeKey: field.attributeKey,
  }
  const childs = []
  const selectedFiles = reactive<Array<SelectedFile>>([])

  const customUploadImageNote = computed(() =>
    isCustomForm.value || isCustomBlogForm.value
      ? field.notes[0].imageUrl
      : productStore.productDescription?.upload_image?.link || ''
  )

  if (field.notes && field.notes.length) {
    field.notes.forEach((note) => {
      if (
        note.type === CUSTOMIZE_FIELD_NOTE_TYPE_IMAGE &&
        customUploadImageNote.value
      ) {
        childs.push(
          h(
            'a',
            {
              'data-fancybox': true,
              'data-src': customUploadImageNote.value,
              class: 'sib-link customize-field-note',
              href: '#',
            },
            note.label
          )
        )
      }
      if (note.type === CUSTOMIZE_FIELD_NOTE_TYPE_TEXT) {
        childs.push(h('div', { innerHTML: note.value }))
      }
      return
    })
  }

  switch (field.type) {
    case CUSTOMIZE_FIELD_TYPE_TEXT:
      tag = Input
      props.maxlength = field.maxlength || '15'
      props.required = true
      break
    case CUSTOMIZE_FIELD_TYPE_TEXTAREA:
      tag = Input
      props.type = 'textarea'
      props.required = true
      break
    case CUSTOMIZE_FIELD_TYPE_DROPDOWN:
      if (
        !field.values?.length &&
        !productStore.productDescription?.upload_image?.quotes
      )
        break
      tag = Select
      props.options = (
        isCustomForm.value || isCustomBlogForm.value
          ? field.values
          : JSON.parse(productStore.productDescription!.upload_image!.quotes)
      ).map((quote: string) => ({
        label: quote,
        value: quote,
      }))
      props.value = props.options[0].value
      break
    case CUSTOMIZE_FIELD_TYPE_RADIO_GROUP:
      if (
        !field.values?.length &&
        !productStore.productDescription?.upload_image?.background
      )
        break
      tag = RadioGroup
      props.group = field.name
      props.options = (
        isCustomForm.value || isCustomBlogForm.value
          ? field.values
          : JSON.parse(
              productStore.productDescription!.upload_image!.background
            )
      ).map((link: string, index: number) => ({
        id: `${field.name}-${index}`,
        name: link,
        value: link,
        image: link,
        sources: [{ size: 100 }],
      }))
      props.showText = false
      props.propertyName = field.name
      props.modelValue = props.options[0].value
      break
    case CUSTOMIZE_FIELD_TYPE_SINGLE_IMAGE:
      tag = FileUpload
      props.required = true
      props.id = field.name
      childs.push(
        h(FileUploadList, {
          fileList: selectedFiles,
          attributeKey: field.attributeKey,
          onDelete,
        })
      )
      break
    case CUSTOMIZE_FIELD_TYPE_MULTIPLE_IMAGE:
      tag = FileUpload
      props.required = true
      props.multiple = true
      props.id = field.name
      props.loading = !productStore.productDescription
      props.onChange = onChange
      childs.push(
        h(FileUploadList, {
          fileList: selectedFiles,
          attributeKey: field.attributeKey,
          onDelete,
        })
      )
      break
    case CUSTOMIZE_FIELD_TYPE_AUDIO:
    case CUSTOMIZE_FIELD_TYPE_SEARCH_SONG:
    case CUSTOMIZE_FIELD_TYPE_AUDIO_OR_SEARCH_SONG:
      tag = CustomPersonalizationAudioOrSearchSpotifySong
      props.id = field.name
      props.multiple = true
      props.type = field.type
      props.label = field.label
      props.labelSearch = field.labelSearch
      props.labelAudio = field.labelAudio
      props.fileList = selectedFiles
      props.onChange = onChange
      props.onDelete = onDelete
      break
    case CUSTOMIZE_FIELD_COLOR_PICKER:
      tag = ColorPicker
      props.type = field.colorPicker
      props.id = field.name
      props.value = field.value
      props.hideProperties = false
      break
    case CUSTOMIZE_FIELD_TYPE_URL:
      tag = SearchCustomUrl
      props.title = field.label
      props.sortId = field.sortId
      props.color = field.color
      props.textSortId = field.textSortId
      break
    default:
      tag = Input
      props.maxlength = 15
      props.required = true
      break
  }

  interface ProcessedFile {
    name: string
    size: number
    link: string
  }
  const processedFiles: Array<ProcessedFile> = []

  const maxNumberOfFiles = computed(() => {
    return (
      ((isCustomForm.value || isCustomBlogForm.value) && field.notes?.length
        ? field.notes[0].imageNumber || field.notes[0].fileNumber || 1
        : productStore.productDescription?.upload_image?.number) || 1
    )
  })

  async function onChange(event: Event) {
    const input = event.target as HTMLInputElement
    if (!input.files) return
    const provider = await Provider.getInstance()
    const newSelectedFiles: Array<SelectedFile> = []
    for (let i = 0; i < input.files.length && i < maxNumberOfFiles.value; i++) {
      const file = input.files[i]
      if (!file) continue
      const processedFile = processedFiles.find(
        (elm) => elm.name === file.name && elm.size === file.size
      )
      const selectedFile: SelectedFile = {
        file,
        propertyName: field.label,
      }
      newSelectedFiles.push(selectedFile)
      if (processedFile) {
        selectedFile.link = processedFile.link
        continue
      }
      const uploadFile =
        field.type == CUSTOMIZE_FIELD_TYPE_MULTIPLE_IMAGE
          ? provider.uploadImage
          : provider.uploadAudio

      uploadFile(file)
        .then((link) => {
          const index = selectedFiles.findIndex((elm) =>
            compareTowSelectedFile(elm, selectedFile)
          )
          if (index > -1) {
            selectedFiles[index].link = link
          } else {
            selectedFile.link = link
          }
          processedFiles.push({
            name: file.name,
            size: file.size,
            link,
          })
        })
        .catch((err) => Logger.log('Error when uploading file', err))
    }
    // merge new files with old files. First in first out
    for (
      let i = selectedFiles.length - 1;
      i >= 0 && newSelectedFiles.length < maxNumberOfFiles.value;
      i--
    ) {
      const selectedFile = selectedFiles[i]
      const index = newSelectedFiles.findIndex((elm) =>
        compareTowSelectedFile(elm, selectedFile)
      )
      if (index == -1) {
        newSelectedFiles.unshift(selectedFile)
      }
    }
    selectedFiles.length = 0
    newSelectedFiles.forEach((file) => {
      selectedFiles.push(file)
    })
    // input.value = ''
  }

  function compareTowSelectedFile(
    selectedFile1: SelectedFile,
    selectedFile2: SelectedFile
  ) {
    return (
      selectedFile1.file.name === selectedFile2.file.name &&
      selectedFile1.file.size === selectedFile2.file.size
    )
  }

  function onDelete(index: number) {
    selectedFiles.splice(index, 1)
  }

  return h('div', [h(tag, props), ...childs])
}
</script>

<style lang="scss">
.custom-options-personalization {
  .custom-options-fields {
    background-color: var(--background-color);
    margin: 1.15em 0 0 0;
    padding: 1.15em;
  }
  .custom-options-fields-customily {
    background-color: var(--background-color);
    padding: 0 1.15em 1.15em 1.15em;
  }
  h2 {
    font-size: 16px;
    font-weight: 600;
  }

  .customize-field-note.sib-link {
    color: var(--color-primary);
    margin-top: 0.5em;
  }

  .product-personalization-no-preview-note {
    color: var(--color-primary);
    font-weight: 500;
  }
}
</style>
